import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Coins } from 'lucide-react'
import { useSession } from '@/shell/session'
import { planLabel } from '@/shell/AppShell'
import { fmtNumber } from '@/lib/format'
import { cn } from '@/lib/utils'
import { summaryQuery } from '@/api/billing'

/**
 * Top-bar plan and credits (billing area). Plan name for everyone; credit
 * balance for people who can see billing; "Upgrade" on Sandbox for people who
 * can change the plan; a warning dot when payment failed, the plan is ending,
 * or the organisation is read-only.
 */
export function PlanHub() {
  const s = useSession()
  const q = useQuery({ ...summaryQuery(s.org?.id ?? ''), enabled: !!s.org })
  if (!s.org) return null
  const d = q.data
  const name = d?.plan_name ?? planLabel(s.org.plan_id)
  const attention = d && d.attention !== 'none'
  return (
    <div className="hidden items-center gap-2 sm:flex" data-testid="plan-hub">
      <Link
        to="/billing"
        className={cn('flex items-center gap-2 rounded-lg border px-2.5 py-1 text-[11.5px] font-semibold text-ink hover:border-accent', attention ? 'border-amber/50' : 'border-line')}
        title={attention ? (d!.attention === 'payment_failed' ? 'A payment failed' : d!.attention === 'ending' ? 'Your plan is set to end' : 'Read-only: over the Sandbox limits') : 'Plan and billing'}
      >
        {attention && <AlertTriangle className="size-3.5 text-amber" aria-label="Needs attention" />}
        <span>{name}</span>
        {d?.credits_balance != null && (
          <span className={cn('flex items-center gap-1 border-l border-line pl-2 font-medium', d.credits_low ? 'text-amber' : 'text-muted')}>
            <Coins className="size-3" aria-hidden />
            {fmtNumber(d.credits_balance)} credits
          </span>
        )}
      </Link>
      {d?.can_upgrade && d.plan_id === 'sandbox' && (
        <Link to="/billing/plans" className="rounded-lg bg-accent px-2.5 py-1 text-[11.5px] font-semibold text-accent-ink hover:opacity-90">
          Upgrade
        </Link>
      )}
    </div>
  )
}
